import { proxy, useSnapshot } from 'valtio';

import { QUERY_PARAM_NATIVE_EMBED_FULLSCREEN } from '@canvas/constants/query-params';

import { getChildClient } from './bridge';

interface NativeEmbedState {
  isFullscreen: boolean;
  isSelected: boolean;
  showTitle: boolean;
}

export const nativeEmbedState = proxy<NativeEmbedState>({
  isFullscreen: false,
  isSelected: false,
  showTitle: true,
});

export function initStateFromUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  nativeEmbedState.isFullscreen = params.has(QUERY_PARAM_NATIVE_EMBED_FULLSCREEN);
}

export function resetStateForTesting(): void {
  nativeEmbedState.isFullscreen = false;
  nativeEmbedState.isSelected = false;
  nativeEmbedState.showTitle = true;
  commandListenersInitialized = false;
}

let commandListenersInitialized = false;

export function initCommandListeners(): void {
  if (commandListenersInitialized || !getChildClient()) {
    return;
  }
  commandListenersInitialized = true;
  handleSelectedChange();
  handleAlwaysShowTitleChange();
  handleViewModeChange();
}

function handleSelectedChange(): void {
  const client = getChildClient();
  if (!client) {
    return;
  }

  client.onSelectedChange(({ selected }) => {
    nativeEmbedState.isSelected = selected;
  });
}

function handleAlwaysShowTitleChange(): void {
  const client = getChildClient();
  if (!client) {
    return;
  }

  client.onAlwaysShowTitleChange(({ alwaysShowTitle }) => {
    nativeEmbedState.showTitle = alwaysShowTitle;
  });
}

function handleViewModeChange(): void {
  const client = getChildClient();
  if (!client) {
    return;
  }

  // The host emits view-mode-change ('maximize' | 'regular') on every
  // inline full-screen transition when running on the no-refresh
  // shared-iframe path (CNS-28621). On the legacy two-iframe path the
  // host remounts the iframe instead, and initStateFromUrl() picks up
  // the nativeEmbedFullscreen query param on that fresh load.
  //
  // TODO(CNS-28595): once @atlassian/native-embeds-core exposes the
  // agreed feature set on NativeEmbedChildClient, defensively gate this
  // subscription on agreed.has(NoRefreshFullScreenPreview) so we ignore
  // stray view-mode events from hosts that didn't actually opt in to
  // the no-refresh contract.
  client.onViewModeChange(({ viewMode }) => {
    nativeEmbedState.isFullscreen = viewMode === 'maximize';
  });
}

export function toggleFullscreen(): void {
  const client = getChildClient();
  if (!client) {
    return;
  }

  client.sendToggleFullScreen();
}

export function requestResize(width?: number, height?: number): void {
  if (!width && !height) {
    return;
  }
  const client = getChildClient();
  if (!client) {
    return;
  }

  client.sendRequestResize(width, height);
}

export function sendRowBoundaries(boundaries: number[]): void {
  const client = getChildClient();
  if (!client) {
    return;
  }

  client.sendRowBoundaries(boundaries);
}

interface UseFullscreenResult {
  isFullscreen: boolean;
  toggleFullscreen: () => void;
}

export function useFullscreen(): UseFullscreenResult {
  const { isFullscreen } = useSnapshot(nativeEmbedState);
  return {
    isFullscreen,
    toggleFullscreen,
  };
}

export function useShowTitle(): boolean {
  const { showTitle } = useSnapshot(nativeEmbedState);
  return showTitle;
}

export function useEmbedSelected(): boolean {
  const { isSelected } = useSnapshot(nativeEmbedState);
  return isSelected;
}
